import React, { FunctionComponent, useState } from "react";

import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { useAppSelector } from "@/redux/hooks";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";
import { scoreSelector } from "@/pages/Private/redux/score/score.slice";

import { DefineIcp } from "../../pages/ListEditDetails/components/DefineIcp";
import { OverrideRules } from "../../pages/ListEditDetails/components/OverrideRules";
import { SearchEngineBadge } from "../../pages/ListEditDetails/components/defineIcp/SearchEngineBadge";
import { SEARCH_ENGINE_BY_POSITION } from "../../pages/ListEditDetails/components/defineIcp/searchProfileEngine";
import { IcpPosition } from "../../pages/ListEditDetails/enum/icp.enum";
import { EnrichmentFilterConfig, Icp } from "../../pages/ListEditDetails/ListEditDetails";
import { List } from "../../schema/list";

import {
	locationOf,
	newProfile,
	PROFILE_SLOTS,
	profileProblemOf,
	ProfileSlot,
} from "./findPeopleDialog.util";
import { PROBLEM_KEY } from "./profileProblemKeys";

interface SearchProfilesEditorProps {
	list: List;
	config?: EnrichmentFilterConfig;
	setConfig: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
}

const SLOT_TITLE_KEY: Record<ProfileSlot, string> = {
	first: "defineIcps.firstSearch",
	second: "defineIcps.secondSearch",
	third: "defineIcps.thirdSearch",
};

const SLOT_INFO_KEY: Record<ProfileSlot, string> = {
	first: "defineIcps.firstSearchInfo",
	second: "defineIcps.secondSearchInfo",
	third: "defineIcps.thirdSearchInfo",
};

/**
 * The bulk find person dialog's search profiles (PREC-4398): each of the three, in the order the
 * search tries them, as a summary with Edit, or "not set" with Add. Edit and Add open the Find
 * person step's own editor (`DefineIcp`) for that one profile, so the dialog edits a profile as the
 * step does; Override Rules is the step's own too. Everything edits the dialog's copy only.
 */
export const SearchProfilesEditor: FunctionComponent<SearchProfilesEditorProps> = ({
	list,
	config,
	setConfig,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`defineIcps.bulkFindPeople.${key}`, options ?? {});
	const [editing, setEditing] = useState<ProfileSlot | null>(null);

	useGetScoreQuery();

	const handleAdd = (slot: ProfileSlot) => {
		setConfig((current) => ({ ...current, [slot]: newProfile() }));
		setEditing(slot);
	};

	return (
		<section className="flex flex-col gap-2.5">
			<div className="text-sm font-bold">
				{ts("profilesTitle")}{" "}
				<span className="font-normal text-text-meta">{ts("profilesHint")}</span>
			</div>

			{PROFILE_SLOTS.map((slot, index) => {
				const profile = config?.[slot];

				if (editing === slot && profile) {
					return (
						<div key={slot} className="flex flex-col" data-testid={`profile-editor-${slot}`}>
							<DefineIcp
								description={t(SLOT_INFO_KEY[slot])}
								list={list}
								prio={slot}
								setTitleFilters={setConfig}
								title={t(SLOT_TITLE_KEY[slot])}
								titleFilters={config}
							/>
							<div className="-mt-8 mb-2 self-end">
								<Button
									color={ButtonColor.ACTION}
									size={ButtonSize.XS}
									title={ts("done")}
									type="button"
									onClick={() => setEditing(null)}
								/>
							</div>
						</div>
					);
				}

				return profile ? (
					<ProfileSummaryRow
						key={slot}
						number={index + 1}
						profile={profile}
						slot={slot}
						onEdit={() => setEditing(slot)}
					/>
				) : (
					<div
						key={slot}
						className="flex items-center justify-between rounded-[10px] border border-dashed border-border-light px-4 py-3.5"
						data-testid={`profile-unset-${slot}`}
					>
						<span className="text-sm text-text-meta">
							<strong className="text-text">{t(SLOT_TITLE_KEY[slot])}</strong> · {ts("notSet")}
						</span>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="w-3 h-3" icon={faPlus} />}
							size={ButtonSize.XS}
							title={ts("add")}
							type="button"
							onClick={() => handleAdd(slot)}
						/>
					</div>
				);
			})}

			<OverrideRules
				rules={config?.overrideRules ?? []}
				onChange={(rules) =>
					setConfig((current) => ({
						...current,
						overrideRules: rules.length > 0 ? rules : undefined,
					}))
				}
			/>
		</section>
	);
};

interface ProfileSummaryRowProps {
	slot: ProfileSlot;
	number: number;
	profile: Icp;
	onEdit: () => void;
}

/** One profile as a line: whom it searches for, where, with which engine, and Edit. */
const ProfileSummaryRow: FunctionComponent<ProfileSummaryRowProps> = ({
	slot,
	number,
	profile,
	onEdit,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`defineIcps.bulkFindPeople.${key}`, options ?? {});
	const { score } = useAppSelector(scoreSelector);
	const problem = profileProblemOf(profile);
	const location = locationOf(profile);

	const whom = (): string => {
		switch (profile.position) {
			case IcpPosition.CEO_AND_SIMILAR:
				return t("defineIcps.defineIcp.ceoAndSimilar");
			case IcpPosition.PERSONA_SEGMENT: {
				const persona = score?.prospectPersonaCategories?.find(
					(category) => category.id === profile.personaCategoryId
				);

				return persona ? ts("persona", { name: persona.category }) : ts("personaUnknown");
			}

			case IcpPosition.CUSTOM:
				return profile.title || ts("noPosition");
			case IcpPosition.DEPARTMENT_SENIORITY:
				return ts("departmentSeniority", {
					value: [...(profile.department ?? []), ...(profile.seniority ?? [])].join(", "),
				});
			default:
				return profile.title || ts("noPosition");
		}
	};

	const place =
		location.kind === "HQ"
			? ts("locationHq")
			: location.kind === "CUSTOM"
				? location.places.join(", ")
				: ts("locationAny");

	return (
		<div
			className="flex items-center gap-4 rounded-[10px] border border-border-light bg-brand-white px-4 py-3.5"
			data-testid={`profile-summary-${slot}`}
		>
			<span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-blue-action-200 text-xs font-bold text-blue-action">
				{number}
			</span>
			<div className="flex flex-1 flex-col gap-0.5">
				<span className="text-sm font-bold">{whom()}</span>
				<span className="text-xs text-text-meta">{ts("location", { place })}</span>
				{problem && (
					<span className="text-xs font-semibold text-status-error">
						{ts("needsInput", { problem: ts(PROBLEM_KEY[problem]) })}
					</span>
				)}
			</div>
			{profile.position && <SearchEngineBadge meta={SEARCH_ENGINE_BY_POSITION[profile.position]} />}
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				size={ButtonSize.XS}
				title={ts("edit")}
				type="button"
				onClick={onEdit}
			/>
		</div>
	);
};
